import { DataTablePresenter, isTabletPortraitDown, ITableColumn, ProviderBankingAccount, Tooltip } from '@enter/core';
import { mdiArrowLeft, mdiCheckCircleOutline, mdiChevronDown, mdiChevronRight, mdiPlus } from '@mdi/js';
import { Icon } from '@mdi/react';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';
import { Row } from 'react-table';
import { DialogContext } from 'services';
import { IObservedRootStore } from 'stores';
import * as S from './shared.styles';

interface IBankingAccount {
    id?: string;
    nicknameOrName?: string;
    institutionName?: string;
    accountType?: string;
    holderName?: string;
    account?: string;
    routing?: string;
    wire?: string;

    forPaymentDeposits?: boolean;
    forFeeCharges?: boolean;
    forMonitoring?: boolean;
    verified?: boolean;
    open?: boolean;
    description?: React.ReactElement;
    Component?: React.ReactElement | (() => React.ReactElement);
}

const TextCell = ({ cell }: any): React.ReactElement => <S.TableCell title={cell.value}>{cell.value}</S.TableCell>;
const BooleanCell = ({ cell }: any): React.ReactElement => (
    <S.TableIconCell>{!!cell.value && <Icon path={mdiCheckCircleOutline} size="18px" />}</S.TableIconCell>
);

const columns: ITableColumn<IBankingAccount>[] = [
    {
        id: 'verified',
        accessor: 'verified',
        disableSortBy: true,
        maxWidth: 32,
        Cell: ({ cell }) => (
            <S.TableIconCell>
                {!!cell.value && (
                    <Tooltip direction="top" title={<span>Verified account</span>}>
                        <Icon path={mdiCheckCircleOutline} size="18px" />
                    </Tooltip>
                )}
            </S.TableIconCell>
        ),
    },
    {
        id: 'nicknameOrName',
        Header: 'Name',
        accessor: 'nicknameOrName',
        disableSortBy: true,
        Cell: TextCell,
    },
    {
        id: 'institutionName',
        Header: 'Institution',
        accessor: 'institutionName',
        disableSortBy: true,
        Cell: TextCell,
    },
    {
        id: 'accountType',
        Header: 'Type',
        accessor: 'accountType',
        disableSortBy: true,
        Cell: TextCell,
    },
    {
        id: 'forPaymentDeposits',
        Header: 'For Payments',
        accessor: 'forPaymentDeposits',
        disableSortBy: true,
        Cell: BooleanCell,
    },
    {
        id: 'forFeeCharges',
        Header: 'For Charges',
        accessor: 'forFeeCharges',
        disableSortBy: true,
        Cell: BooleanCell,
    },
    {
        id: 'forMonitoring',
        Header: 'For Monitoring',
        accessor: 'forMonitoring',
        disableSortBy: true,
        Cell: BooleanCell,
    },
    {
        id: 'selectable',
        maxWidth: 40,
        Cell: ({ cell }): React.ReactElement => {
            const path = cell.row.isExpanded ? mdiChevronDown : mdiChevronRight;
            return (
                <S.TableIconCell>
                    <Icon path={path} size="18px" />
                </S.TableIconCell>
            );
        },
    },
];

export const Banking: React.FunctionComponent<IObservedRootStore> = inject('rootStore')(
    observer(
        (props: IObservedRootStore): React.ReactElement => {
            const { rootStore } = props;
            if (!rootStore) {
                return <></>;
            }
            const dialogContext = useContext(DialogContext);
            const [isDone, setDone] = useState(false);
            const [isLoading, setLoading] = useState(false);
            const [waitingFor, setWaitingFor] = useState<'add' | 'charge' | 'deposit' | 'monitoring'>();
            const [presenter] = useState(
                new DataTablePresenter({
                    name: 'banking',
                    data: [],
                    columns,
                    filterSchemas: [],
                }),
            );
            presenter.saveTableDefaults({
                ...presenter.getTableDefaults(),
                pageSize: 100,
            });

            useEffect(() => {
                if (isDone || isLoading) {
                    return;
                }
                (async () => {
                    setLoading(true);
                    const accounts = await rootStore.settingsStore.fetchBankingAccounts();
                    presenter.onDataUpdate(convertAccounts(accounts));
                    presenter.onDataTotalChange(accounts.length);
                    setDone(true);
                    setLoading(false);
                })();
            }, [isDone, isLoading, presenter, rootStore]);

            const convertAccounts = (accounts: ProviderBankingAccount[]): IBankingAccount[] => {
                const bankingAccounts = _.map(accounts, account => {
                    return {
                        id: account.id,
                        nicknameOrName: account.nickname || account.name,
                        institutionName: account.institutionName,
                        accountType: account.accountType,
                        holderName: account.holderName,
                        account: account.account,
                        routing: account.routing,
                        wire: account.wire,

                        forPaymentDeposits: account.forPaymentDeposits,
                        forMonitoring: account.forMonitoring,
                        forFeeCharges: account.forFeeCharges,
                        verified: account.verified,
                        Component: <div>{account.account}</div>,
                    };
                });
                return bankingAccounts;
            };

            const setChargeAccount = (id: string) => {
                return async () => {
                    setWaitingFor('charge');
                    try {
                        await rootStore.settingsStore.setChargeAccount(id);
                        setDone(false);
                    } catch (err) {
                        dialogContext.showError(err);
                    }
                    setWaitingFor(undefined);
                };
            };

            const setDepositAccount = (id: string) => {
                return async () => {
                    setWaitingFor('deposit');
                    try {
                        await rootStore.settingsStore.setDepositAccount(id);
                        setDone(false);
                    } catch (err) {
                        dialogContext.showError(err);
                    }
                    setWaitingFor(undefined);
                };
            };

            const setMonitoringAccount = (id: string) => {
                return async () => {
                    setWaitingFor('monitoring');
                    try {
                        await rootStore.settingsStore.setMonitoringAccount(id);
                        setDone(false);
                    } catch (err) {
                        dialogContext.showError(err);
                    }
                    setWaitingFor(undefined);
                };
            };

            const addAccount = async (transactionsOnly: boolean) => {
                setWaitingFor('add');
                try {
                    const shortUrl = await rootStore.settingsStore.getAddAccountUrl(transactionsOnly);
                    if (shortUrl && shortUrl.url) {
                        window.open(shortUrl.url, '_blank');
                    }
                } catch (err) {
                    dialogContext.showError(err);
                }
                setWaitingFor(undefined);
            };

            const onRowClick = (row: Row<IBankingAccount>): void => {
                if (row.original.Component) {
                    row.toggleRowExpanded();
                }
            };

            const renderExpanded = (row: Row<IBankingAccount>): React.ReactElement => {
                if (row.isExpanded) {
                    return (
                        <S.TableExpanded>
                            <S.TableExpandedCol>
                                <S.TableExpandedRow>
                                    <S.TableExpandedItem>
                                        <S.TableHeader>Account Holder:</S.TableHeader> {row.original.holderName}
                                    </S.TableExpandedItem>
                                    <S.TableExpandedItem>
                                        <S.TableHeader>Account #:</S.TableHeader> {row.original.account}
                                    </S.TableExpandedItem>
                                    <S.TableExpandedItem>
                                        <S.TableHeader>Routing #:</S.TableHeader> {row.original.routing}
                                    </S.TableExpandedItem>
                                    <S.TableExpandedItem>
                                        <S.TableHeader>Wire #:</S.TableHeader> {row.original.wire}
                                    </S.TableExpandedItem>
                                </S.TableExpandedRow>
                                {row.original.id && (
                                    <S.TableExpandedRow align="right">
                                        <S.Button
                                            disabled={row.original.forMonitoring || waitingFor === 'monitoring'}
                                            variant="outlined"
                                            onClick={setMonitoringAccount(row.original.id)}>
                                            {waitingFor === 'monitoring' && <S.Waiting size="18px" />}
                                            Use this account for monitoring
                                        </S.Button>
                                        <S.Button
                                            disabled={row.original.forPaymentDeposits || waitingFor === 'deposit'}
                                            variant="outlined"
                                            onClick={setDepositAccount(row.original.id)}>
                                            {waitingFor === 'deposit' && <S.Waiting size="18px" />}
                                            Use this account for payment deposits
                                        </S.Button>
                                        <S.Button
                                            disabled={row.original.forFeeCharges || waitingFor === 'charge'}
                                            variant="outlined"
                                            onClick={setChargeAccount(row.original.id)}>
                                            {waitingFor === 'charge' && <S.Waiting size="18px" />}
                                            Use this account for fee charges
                                        </S.Button>
                                    </S.TableExpandedRow>
                                )}
                            </S.TableExpandedCol>
                        </S.TableExpanded>
                    );
                }
                return <></>;
            };

            return (
                <S.SubCategoryCol>
                    <S.SubCategoryItem>
                        <S.SubCategoryItemRow>
                            <S.SubCategoryH1>
                                {isTabletPortraitDown() && (
                                    <S.ReturnLink to="/settings">
                                        {(): React.ReactElement => <Icon path={mdiArrowLeft} size={1} />}
                                    </S.ReturnLink>
                                )}
                                Banking
                            </S.SubCategoryH1>
                            <S.SubCategoryAddButton
                                disabled={waitingFor === 'add'}
                                variant="primary"
                                onClick={() => addAccount(false)}>
                                {waitingFor === 'add' ? (
                                    <S.Waiting size="18px" />
                                ) : (
                                    <S.SubCategoryPlus path={mdiPlus} size={1} />
                                )}{' '}
                                Add account
                            </S.SubCategoryAddButton>
                        </S.SubCategoryItemRow>
                        <S.Table
                            presenter={presenter}
                            isLoading={isLoading}
                            onRowClick={onRowClick}
                            renderRowSubComponent={renderExpanded}
                            hideFiltering={true}
                            hidePagination={true}
                            hideSettings={true}
                        />
                    </S.SubCategoryItem>
                </S.SubCategoryCol>
            );
        },
    ),
);
